import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

// Store
import { Dispatch, RootState } from '../../store/store';

// Components
import Portal from '../Portal/Portal';
import CycleTable from '../CycleTable/CycleTable/CycleTable';
import SpinnerLoader from '../SpinnerLoader/SpinnerLoader';

// Hooks
import { useIntervalOnVisibility } from '../../hooks/useIntervalOnVisibility';

// Constants
import { INTERVAL_UPDATE_DATA } from '../../constants/common';

// Utils
import getMeasurementPreference from '../../utils/getMeasurementPreference';

// Types
import { CycleItem } from '../Cycle/types';

const VIEWPORT_MARGIN = 8;

type ComponentProps = {
  cycleId: string;
  cycleName: string;
  isVisible: boolean;
  onClose: () => void;
  anchorRef: React.RefObject<HTMLElement>;
} & ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

const CycleTablePreviewModal: React.FC<ComponentProps> = ({
  cycleId,
  isVisible,
  onClose,
  anchorRef,
  measurement,
  getOneWithLatestIot,
}) => {
  const [cycleItem, setCycleItem] = useState<CycleItem | null>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const popupRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!isVisible) {
      setCycleItem(null);
      return;
    }

    const rect = anchorRef.current?.getBoundingClientRect();
    if (rect) {
      setPosition({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX });
    }

    getOneWithLatestIot(cycleId).then(setCycleItem);
  }, [isVisible, cycleId]);

  // Keep the table live while the popup is open - polling stops as soon as
  // it's closed (isVisible becomes false) via the hook's own enabled check.
  useIntervalOnVisibility({
    callback: () => {
      getOneWithLatestIot(cycleId).then(setCycleItem);
    },
    delay: INTERVAL_UPDATE_DATA,
    dependencies: [cycleId],
    enabled: isVisible,
  });

  // Once the table has content and a real size, nudge the popup back inside
  // the viewport if the anchor was near the right/bottom edge.
  useLayoutEffect(() => {
    if (!isVisible || !popupRef.current) {
      return;
    }

    const rect = popupRef.current.getBoundingClientRect();
    setPosition((prev) => {
      let { top, left } = prev;

      const overflowRight = rect.left + rect.width - (window.scrollX + window.innerWidth - VIEWPORT_MARGIN);
      if (overflowRight > 0) {
        left -= overflowRight;
      }

      const overflowBottom = rect.top + rect.height - (window.scrollY + window.innerHeight - VIEWPORT_MARGIN);
      if (overflowBottom > 0) {
        top -= overflowBottom;
      }

      left = Math.max(left, window.scrollX + VIEWPORT_MARGIN);
      top = Math.max(top, window.scrollY + VIEWPORT_MARGIN);

      return top === prev.top && left === prev.left ? prev : { top, left };
    });
  }, [isVisible, cycleItem]);

  // Close on an outside click - matched on both mousedown and mouseup (not a
  // single "click") so a text-selection drag starting inside the popup and
  // released outside it doesn't close the popup. Clicks inside another open
  // modal (e.g. the channel settings popup opened from a row in this table)
  // are ignored too, since that content is portaled outside this popup's DOM.
  useEffect(() => {
    if (!isVisible) {
      return;
    }

    const isOutside = (target: Node) => {
      if (popupRef.current?.contains(target)) return false;
      if (anchorRef.current?.contains(target)) return false;
      if (target instanceof HTMLElement && target.closest('.c-modal__overlay')) return false;
      return true;
    };

    let mouseDownOutside = false;

    const handleMouseDown = (e: MouseEvent) => {
      mouseDownOutside = isOutside(e.target as Node);
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (mouseDownOutside && isOutside(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isVisible, onClose]);

  if (!isVisible) {
    return null;
  }

  return (
    <Portal>
      <div
        ref={popupRef}
        className="absolute z-9999 max-h-[90vh] max-w-[95vw] overflow-auto rounded border-[5px] border-black bg-white dark:bg-boxdark"
        style={{ top: position.top, left: position.left }}
      >
        {!cycleItem || !measurement ? (
          <div className="flex w-full items-center justify-center p-10">
            <SpinnerLoader className="h-10 w-10" />
          </div>
        ) : (
          <CycleTable
            cycleId={cycleItem.id}
            cycle_controllers={cycleItem.cycle_controllers}
            iotDataResponse={cycleItem.iotDataResponse}
            deviceNames={cycleItem.deviceNames}
            controllersSettings={cycleItem.controllersSettings}
            status={cycleItem.status}
            measurement={measurement}
            channelsIot={cycleItem.channelsIot}
          />
        )}
      </div>
    </Portal>
  );
};

const mapState = (state: RootState) => ({
  measurement: getMeasurementPreference(state.contracts.contract, state.locations.currentLocation),
});

const mapDispatch = (dispatch: Dispatch) => ({
  getOneWithLatestIot: dispatch.cycles.getOneWithLatestIot,
});

export default connect(mapState, mapDispatch)(CycleTablePreviewModal);
